import {filterMyTasksScope} from "../../models/task/my-tasks-scope";

const priorityWeight = {critical: 4, high: 4, important: 3, medium: 2, normal: 1, low: 0};

export function normalizeDueDate(value) {
    if (!value) return null;
    const date = typeof value === "number" ? new Date(value < 1000000000000 ? value * 1000 : value) : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

export function getTaskPriority(task) {
    return task?.priority || task?.taskPriority || null;
}

export function isFocusTaskOverdue(task, now = new Date()) {
    const dueDate = task?.focusDueDate || normalizeDueDate(task?.dueDate);
    if (!dueDate) return false;
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    return dueDate < startOfToday;
}

export function getDashboardFocusTasks(items, currentSession) {
    const sourceItems = Array.isArray(items) ? items.filter((item) => item?.task) : [];
    const activeTask = ["running", "paused"].includes(currentSession?.focusSessionStatus)
        ? currentSession?.task
        : null;
    const activeTaskKey = activeTask?.documentId ?? activeTask?.id;
    const persistedTasks = sourceItems.map((item, index) => {
        const taskKey = item.task?.documentId ?? item.task?.id;
        return {
            ...item.task,
            __focusItemId: item.focusItemId,
            __focusCapabilities: item.capabilities,
            __focusPosition: index,
            __focusTotal: sourceItems.length,
            __isActiveFocusTask: activeTaskKey !== null && activeTaskKey !== undefined
                && String(taskKey) === String(activeTaskKey),
        };
    });
    if (activeTaskKey === null || activeTaskKey === undefined || persistedTasks.some((task) => task.__isActiveFocusTask)) {
        return persistedTasks;
    }
    return [{
        ...activeTask,
        __focusItemId: null,
        __focusCapabilities: {canRemove: false, canReorder: false, canStart: false},
        __focusPosition: null,
        __focusTotal: sourceItems.length,
        __isActiveFocusTask: true,
    }, ...persistedTasks];
}

export function getAttentionTasks(tasks, workspace, membership, now = new Date()) {
    const startOfTomorrow = new Date(now);
    startOfTomorrow.setHours(0, 0, 0, 0);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

    return filterMyTasksScope(tasks, workspace, membership)
        .filter((task) => !["done", "completed"].includes(task.taskStatus))
        .filter((task) => !task.deletedAt && !task.archivedAt)
        .map((task, index) => ({...task, attentionIndex: index, attentionDueDate: normalizeDueDate(task.dueDate)}))
        .filter((task) => task.taskStatus === "blocked"
            || ["critical", "high"].includes(getTaskPriority(task))
            || (task.attentionDueDate && task.attentionDueDate < startOfTomorrow))
        .sort((left, right) => {
            if ((left.taskStatus === "blocked") !== (right.taskStatus === "blocked")) return left.taskStatus === "blocked" ? -1 : 1;
            const priorityDifference = (priorityWeight[getTaskPriority(right)] || 0) - (priorityWeight[getTaskPriority(left)] || 0);
            if (priorityDifference) return priorityDifference;
            if (left.attentionDueDate && right.attentionDueDate) {
                const dueDifference = left.attentionDueDate - right.attentionDueDate;
                if (dueDifference) return dueDifference;
            } else if (left.attentionDueDate || right.attentionDueDate) {
                return left.attentionDueDate ? -1 : 1;
            }
            return left.attentionIndex - right.attentionIndex;
        });
}

export function getCriticalTaskCount(tasks, workspace, membership) {
    return filterMyTasksScope(tasks, workspace, membership)
        .filter((task) => !["done", "completed"].includes(task.taskStatus))
        .filter((task) => !task.deletedAt && !task.archivedAt)
        .filter((task) => ["critical", "high"].includes(getTaskPriority(task)))
        .length;
}

function sameIdentity(left, right) {
    const leftValues = [left?.documentId, left?.id, typeof left !== "object" ? left : null].filter(Boolean).map(String);
    const rightValues = new Set([right?.documentId, right?.id, typeof right !== "object" ? right : null].filter(Boolean).map(String));
    return !leftValues.length || !rightValues.size || leftValues.some((value) => rightValues.has(value));
}

export function getActionRequiredItems(items, workspace, user) {
    const seen = new Set();
    return (Array.isArray(items) ? items : [])
        .filter((item) => sameIdentity(item.workspace, workspace))
        .filter((item) => sameIdentity(item.owner, user))
        .filter((item) => item.eventCategory === "mention" && item.requiresResponse === true)
        .filter((item) => !["responded", "dismissed", "resolved"].includes(item.responseState))
        .filter((item) => !item.deletedAt && !item.archivedAt)
        .filter((item) => {
            const key = item.documentId ?? item.id;
            if (key === null || key === undefined || seen.has(String(key))) return false;
            seen.add(String(key));
            return true;
        })
        .sort((left, right) => new Date(left.createdAt || 0) - new Date(right.createdAt || 0));
}
